import { gql, useQuery } from '@apollo/client';
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import {
  AlertBanner,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  IconButton,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useMemo, useState } from 'react';
import { matchPath, useLocation, useRouteMatch } from 'react-router';
import { Link, NavLink, Route, Switch } from 'react-router-dom';
import { Navigate } from 'react-router-dom-v5-compat';
import {
  ApolloGraphqlActionsProvider,
  ApolloGraphqlDiagnosticsProvider,
} from 'register-apollo-graphql-language';

import * as orgRouteConfig from 'src/app/account/routes';
import { GlobalHeader } from 'src/app/appLayout/components/globalHeader/GlobalHeader';
import { OrgSwitcher } from 'src/components/appNav/orgSwitcher/OrgSwitcher';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { SuperAdminWarningBanner } from 'src/components/common/superAdminWarningBanner/SuperAdminWarningBanner';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { PlanProtectedArea } from 'src/components/planProtectedArea/PlanProtectedArea';
import { Toasts } from 'src/components/toast/Toast';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { dateCompare } from 'src/lib/sort';

import { ProposalCheckLayout } from '../check/ProposalCheckLayout';
import { useInternalGraphLinking } from '../hooks/useInternalGraphLinking';
import {
  graphProposalListRouteConfig,
  proposalChangesRouteConfig,
  proposalCheckRouteConfig,
  proposalChecksListRouteConfig,
  proposalChecksRouteConfig,
  proposalEditorRouteConfig,
  proposalHomepageRouteConfig,
  proposalRouteConfig,
  proposalSchemaRouteConfig,
} from '../routes';
import { STUDIO_UI_GRAPH_FLAGS } from '../variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { NonPublicGraphArea } from '../variantLayout/NonPublicGraphArea';
import { VariantLayoutModals } from '../variantLayout/VariantLayoutModals';
import { VariantLayoutProviders } from '../variantLayout/VariantLayoutProviders';

import { ConflictBadgeWithLink } from './ConflictBadge';
import { ProposalChanges } from './proposalChanges/ProposalChanges';
import { ProposalChecks } from './proposalChecks/ProposalChecks';
import { ProposalEditor } from './proposalEditor/ProposalEditor';
import { ProposalHomepage } from './proposalHomepage/ProposalHomepage';
import {
  ProposalReviewModal,
  ProposalReviewModalProposalReviewFragment,
} from './proposalHomepage/proposalReviewModal/ProposalReviewModal';
import { RenameProposalModal } from './proposalHomepage/renameProposalModal/RenameProposalModal';
import { RequestedReviewerBanner } from './proposalHomepage/requestedReviewersSection/RequestedReviewerBanner';
import { ReviewerMultiselectPopoverUsersServiceFragment } from './proposalHomepage/requestedReviewersSection/ReviewerMultiselectPopover';
import { ProposalStatusBadge } from './ProposalStatusBadge';
import { UpgradeFederationButton } from './upgradeFederationButton/UpgradeFederationButton';
import { useIsProposalStale } from './useIsProposalStale';
import { useRecentPublishChecksToast } from './useRecentPublishChecksToast';
import { useRecentPublishViewChangesToast } from './useRecentPublishViewChangesToast';

const DocumentationPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "DocumentationPage" */ '../documentationPage/DocumentationPage'
    )
  ).DocumentationPage,
}));

export const ProposalLayout = () => {
  const location = useLocation();
  const [currentAccountId, setCurrentAccountId] = useCurrentAccountId();
  const { graphId, graphVariant, pullUpstreamChanges } =
    useRouteParams(proposalRouteConfig);
  const graphRef = { graphId, graphVariant };

  const { data: userData, loading: userLoading } = useQuery(
    gql<
      GraphQLTypes.ProposalLayoutUserQuery,
      GraphQLTypes.ProposalLayoutUserQueryVariables
    >`
      query ProposalLayoutUserQuery {
        me {
          id
          ... on User {
            internalAdminRole
            memberships {
              account {
                id
              }
              user {
                id
              }
            }
          }
        }
      }
    `,
    { context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }) },
  );

  const {
    data: graphData,
    loading: graphLoading,
    error: graphLoadingError,
  } = useQuery<
    GraphQLTypes.ProposalLayoutGraphQuery,
    GraphQLTypes.ProposalLayoutGraphQueryVariables
  >(
    gql`
      query ProposalLayoutGraphQuery($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          title
          graphType
          defaultProposalReviewers {
            id
          }
          proposalsMustBeApprovedByADefaultReviewer
          variant(name: $graphVariant) {
            id
            name
            url
            isProposal
            hasManagedSubgraphs
            proposal {
              id
              rebaseConflicts {
                ... on RebaseConflictData {
                  count
                }
              }
              displayName
              requestedReviewers {
                user {
                  id
                }
                currentReview {
                  id
                  decision
                  isDismissed
                }
              }
              reviews {
                id
                ...ProposalReviewModalProposalReviewFragment
              }
              status
              canEditProposal
              latestRevision {
                id
              }
              createdBy {
                id
              }
              sourceVariant {
                id
                name
                compositionVersion
              }
              mergeBaseCompositionId
              hasUpstreamChanges
            }
            activeSchemaPublish {
              id
              historyLength
            }
            compositionVersion
          }
          account {
            id
          }
        }
      }
      ${ProposalReviewModalProposalReviewFragment}
    `,
    {
      variables: graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  // Preload permissions so we can consistently show blocks instead of flashing no permission errors
  const { loading: loadingPermissions, data: permissionData } = usePermissions(
    graphData?.service?.account?.id,
    graphId,
    graphVariant,
  );

  const { isStale, setInitialLatestRevisionId } = useIsProposalStale({
    graphRef,
  });

  useRecentPublishChecksToast({
    canQueryLaunches: permissionData.graphVariantPermissions?.canQueryLaunches,
  });

  const { showPublishRevisionToast } = useRecentPublishViewChangesToast();

  const isProposal = graphData?.service?.variant?.isProposal;
  const me = userData?.me;
  const graph = graphData?.service;
  const variant = graph?.variant;
  const sourceVariant = variant?.proposal?.sourceVariant;
  const accountId = graph?.account?.id ?? null;
  const isEditorRoute = useRouteMatch(proposalEditorRouteConfig.definition);
  const isHomeRoute = useRouteMatch(proposalHomepageRouteConfig.definition);
  const isPullUpstreamOn =
    useLDFlag('nebula_proposals_merge_upstream') &&
    !!variant?.proposal?.mergeBaseCompositionId;
  const isProposalClosedOrImplemented =
    variant?.proposal?.status === ProposalStatus.CLOSED ||
    variant?.proposal?.status === ProposalStatus.IMPLEMENTED;

  const isOnPullUpstreamMode =
    (isEditorRoute && pullUpstreamChanges === 'true' && isPullUpstreamOn) ||
    false;

  const hasFedVersionMismatch =
    useLDFlag('gov-upgrade-proposal-variant-fed-version') &&
    variant?.compositionVersion !== sourceVariant?.compositionVersion;

  const { locationToReference } = useInternalGraphLinking();
  // Only set this when accountId is truthy to avoid an infinite render
  // cycle where this nulls out `currentAccountId` and then `useIdentity`
  // sets it back to the users default org.
  React.useEffect(() => {
    if (accountId) {
      setCurrentAccountId(accountId);
    }
  }, [setCurrentAccountId, accountId]);

  const latestRevision = variant?.proposal?.latestRevision;

  const shouldShowRequestedReviewBanner =
    isHomeRoute &&
    variant?.proposal?.status !== GraphQLTypes.ProposalStatus.IMPLEMENTED &&
    latestRevision &&
    variant?.proposal?.requestedReviewers.some(
      (reviewRequest) => reviewRequest?.user?.id === me?.id,
    );
  const reviewButtonDisabled =
    variant?.proposal?.createdBy?.id === me?.id &&
    (!graph?.proposalsMustBeApprovedByADefaultReviewer ||
      !graph?.defaultProposalReviewers.some(
        (defaultReviewer) => defaultReviewer?.id === me?.id,
      ));
  const canEditProposal = !!variant?.proposal?.canEditProposal;
  const currentUserReview = useMemo(() => {
    return variant?.proposal?.reviews
      .filter((review) => review.createdBy?.id === me?.id)
      .sort((a, b) => dateCompare(a?.createdAt, b.createdAt, false))[0];
  }, [me?.id, variant?.proposal?.reviews]);

  const [subgraphsHaveChanges, setSubgraphsHaveChanges] = useState(false);

  const { data: membershipsData } = useQuery<
    GraphQLTypes.RequestedReviewersSectionMemberships,
    GraphQLTypes.RequestedReviewersSectionMembershipsVariables
  >(
    gql`
      query RequestedReviewersSectionMemberships($graphId: ID!) {
        service(id: $graphId) {
          id
          ...ReviewerMultiselectPopoverUsersServiceFragment
        }
      }
      ${ReviewerMultiselectPopoverUsersServiceFragment}
    `,
    {
      variables: { graphId },
      skip: !canEditProposal && !graphLoading,
    },
  );
  const shouldShowReviewButton =
    !shouldShowRequestedReviewBanner &&
    variant?.proposal?.status !== GraphQLTypes.ProposalStatus.DRAFT &&
    variant?.proposal?.status !== GraphQLTypes.ProposalStatus.IMPLEMENTED &&
    canEditProposal &&
    me?.id !== variant?.proposal?.createdBy?.id &&
    membershipsData?.service?.account?.memberships
      ?.map(({ user }) => user)
      .some((user) => user.id === me?.id);

  const hasConflicts =
    variant?.proposal?.rebaseConflicts?.__typename === 'RebaseConflictData' &&
    variant.proposal.rebaseConflicts.count !== 0;

  const isPullUpstreamButtonDisabled =
    !canEditProposal ||
    subgraphsHaveChanges ||
    isOnPullUpstreamMode ||
    !variant?.proposal?.hasUpstreamChanges ||
    isProposalClosedOrImplemented;

  if (graphLoading || userLoading || loadingPermissions) {
    return <Loading />;
  }

  // If the graph has *any* public variants we will be able to fetch the graph,
  // what we care about should be if we have access to this *variant*
  if (!variant || !accountId) {
    if (graphLoadingError !== undefined) {
      return (
        <NotFound
          image="satellite"
          title="Try refreshing"
          subtitle={
            <>
              <div className="mb-8">
                There was an error loading your Proposal. Try refreshing the
                page.
              </div>
            </>
          }
        />
      );
    }
    return (
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <GlobalHeader leftNavContext="collapsed" />
        <div className="flex h-full flex-1 overflow-y-auto">
          <NotFound
            image="satellite"
            title={
              graphLoadingError
                ? 'Try refreshing'
                : 'Oops, no sign of life here...'
            }
            subtitle={
              <>
                <div className="mb-8">
                  {graphLoadingError ? (
                    'There was an error loading your Proposal. Try refreshing the page.'
                  ) : (
                    <>
                      The graph you are looking for could not be found. Please
                      go back to the <b>Graph List</b> and select another graph.
                    </>
                  )}
                </div>
                <Button leftIcon={<IconArrowLeft />} as={Link} to="/">
                  Graph List
                </Button>
              </>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <VariantLayoutProviders
      graphRef={graphRef}
      graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
    >
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <GlobalHeader
          leftNavContext="collapsed"
          goHomeLink={
            graph.account?.id
              ? orgRouteConfig.org.path({ orgId: graph.account.id })
              : undefined
          }
        >
          <OrgSwitcher collapseOrgName={true} />
          <Breadcrumb
            listProps={{
              className: classnames('flex'),
            }}
          >
            <BreadcrumbItem
              isCurrentPage={true}
              className="overflow-auto text-ellipsis whitespace-nowrap"
            >
              <BreadcrumbLink
                as={NavLink}
                to={{
                  ...orgRouteConfig.graphs.location({ orgId: accountId }),
                  state: { highlightSupergraphState: { graphId } },
                }}
                className="text-primary"
              >
                {graph?.title}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem className="overflow-auto text-ellipsis whitespace-nowrap">
              <BreadcrumbLink
                className="overflow-auto text-ellipsis"
                as={Link}
                to={graphProposalListRouteConfig.locationFrom({
                  location,
                  patch: {
                    filterByStatuses: [
                      GraphQLTypes.ProposalStatus.OPEN,
                      GraphQLTypes.ProposalStatus.DRAFT,
                      GraphQLTypes.ProposalStatus.APPROVED,
                    ],
                  },
                  fromRouteConfig: proposalRouteConfig,
                })}
              >
                proposals
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              isCurrentPage={true}
              className="overflow-auto text-ellipsis whitespace-nowrap"
            >
              <span className="overflow-auto text-ellipsis">
                {variant.proposal?.displayName ?? graphVariant}
              </span>
            </BreadcrumbItem>
          </Breadcrumb>
        </GlobalHeader>
        {isStale && (
          <AlertBanner status="warning">
            This proposal has been updated
            {!isEditorRoute
              ? '. Please refresh to see the latest changes.'
              : " since you started editing. You'll need to save your changes separately and apply them after refreshing the page."}
          </AlertBanner>
        )}
        <PlanProtectedArea
          accountId={accountId}
          redirectExpiredToOrg={true}
          requiresPlanCapability="proposals"
        >
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryProposals"
            fallback={PermissionBlockCard}
          >
            <div className="flex h-1 flex-1 flex-col">
              <Toasts />
              <PageTitle
                headingClassName="group/proposal-title"
                title={variant.proposal?.displayName ?? graphVariant}
                rightSide={
                  <div>
                    {hasFedVersionMismatch && variant?.hasManagedSubgraphs && (
                      <UpgradeFederationButton
                        graphRef={graphRef}
                        canManageBuildConfig={
                          !!permissionData.graphVariantPermissions
                            ?.canManageBuildConfig
                        }
                      />
                    )}
                    {isPullUpstreamOn && (
                      <>
                        <Tooltip
                          className="max-w-sm"
                          label={
                            !canEditProposal
                              ? 'You do not have permission to pull changes from the source variant for this proposal.'
                              : isOnPullUpstreamMode
                                ? 'This is the merge result. Please resolve any conflicts, and save revision to complete pulling changes from source.'
                                : !variant?.proposal?.hasUpstreamChanges
                                  ? `This proposal is up to date with ${variant.proposal?.sourceVariant?.id}.`
                                  : subgraphsHaveChanges
                                    ? 'Please save your local changes before pulling changes from the source variant.'
                                    : hasConflicts
                                      ? `Pulling changes from ${variant.proposal?.sourceVariant?.id} will result in conflicts that will need to be resolved.`
                                      : isProposalClosedOrImplemented
                                        ? `${variant.proposal?.status === ProposalStatus.CLOSED ? 'Closed' : 'Implemented'} proposals cannot pull changes`
                                        : `Pull changes from ${variant.proposal?.sourceVariant?.id}, if any exist`
                          }
                        >
                          <Button
                            className="m-2"
                            isDisabled={isPullUpstreamButtonDisabled}
                            as={Link}
                            to={proposalEditorRouteConfig.locationFrom({
                              location,
                              fromRouteConfig: proposalRouteConfig,
                              patch: {
                                pullUpstreamChanges: 'true',
                              },
                            })}
                            data-testid="proposals_pull_changes_button"
                          >
                            {!isOnPullUpstreamMode
                              ? 'Pull Changes'
                              : 'Merged schemas displayed'}
                          </Button>
                        </Tooltip>
                      </>
                    )}
                    {shouldShowReviewButton && (
                      <Button
                        className="m-2"
                        isDisabled={reviewButtonDisabled}
                        as={Link}
                        to={setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.addReview,
                        })}
                      >
                        Add review
                      </Button>
                    )}
                  </div>
                }
                tabs={
                  <>
                    <HeaderTab
                      // 🚨 Do not change the name of this tab - we rely on it for our Proposals Browser test
                      // TODO(Maya) add link to synthetics test
                      tabID="Overview"
                      to={proposalHomepageRouteConfig.locationFrom({
                        location,
                        fromRouteConfig: proposalRouteConfig,
                        patch: {
                          pullUpstreamChanges: RouteConfig.DELETE,
                        },
                      })}
                    />
                    <HeaderTab
                      // 🚨 Do not change the name of this tab - we rely on it for our Proposals Browser test
                      // TODO(Maya) add link to synthetics test
                      tabID="Editor"
                      to={proposalEditorRouteConfig.locationFrom({
                        location,
                        fromRouteConfig: proposalRouteConfig,
                      })}
                    />
                    <HeaderTab
                      // 🚨 Do not change the name of this tab - we rely on it for our Proposals Browser test
                      // TODO(Maya) add link to synthetics test
                      tabID="Changes"
                      to={proposalChangesRouteConfig.locationFrom({
                        location,
                        fromRouteConfig: proposalRouteConfig,
                        patch: {
                          pullUpstreamChanges: RouteConfig.DELETE,
                          selectedSchema: RouteConfig.DELETE,
                        },
                      })}
                    />
                    <HeaderTab
                      // 🚨 Do not change the name of this tab - we rely on it for our Proposals Browser test
                      // TODO(Maya) add link to synthetics test
                      tabID="Schema Reference"
                      to={locationToReference({})}
                    />
                    <PermissionGuard
                      graphId={graph?.id}
                      graphVariant={graphVariant}
                      graphVariantPermissions="canQueryLaunches"
                      fallback={null}
                    >
                      <HeaderTab
                        tabID="Checks"
                        to={proposalChecksRouteConfig.locationFrom({
                          location,
                          fromRouteConfig: proposalRouteConfig,
                          patch: {
                            pullUpstreamChanges: RouteConfig.DELETE,
                          },
                        })}
                        isActive={() =>
                          [
                            proposalChecksRouteConfig,
                            proposalCheckRouteConfig,
                          ].some((route) =>
                            matchPath(location.pathname, {
                              path: route.definition,
                              exact: false,
                            }),
                          )
                        }
                      />
                    </PermissionGuard>
                  </>
                }
              >
                <div>
                  {variant.proposal?.displayName ?? graphVariant}
                  {variant.proposal?.status && (
                    <ProposalStatusBadge
                      status={variant.proposal.status}
                      className="ml-2 align-middle"
                    />
                  )}
                  {isPullUpstreamOn &&
                    variant.proposal?.status !== ProposalStatus.IMPLEMENTED &&
                    variant.proposal?.status !== ProposalStatus.CLOSED &&
                    hasConflicts && (
                      <ConflictBadgeWithLink
                        className="ml-2 align-middle"
                        isDisabled={isPullUpstreamButtonDisabled}
                        graphVariantName={variant.name}
                      />
                    )}
                  {canEditProposal ? (
                    <Tooltip label="Rename Proposal" placement="right">
                      <IconButton
                        variant="hidden"
                        className="ml-2 opacity-0 focus-visible:opacity-100 group-hover/proposal-title:opacity-100"
                        icon={<IconEdit />}
                        aria-label="Rename Proposal"
                        as={Link}
                        to={setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.renameProposalTitle,
                        })}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      label="You do not have access to edit this proposal"
                      placement="right"
                    >
                      <IconButton
                        variant="hidden"
                        isDisabled={true}
                        className="ml-2 opacity-0 focus-visible:opacity-100 group-hover/proposal-title:opacity-100"
                        icon={<IconEdit />}
                        aria-label="You do not have access to edit this proposal"
                      />
                    </Tooltip>
                  )}
                </div>
              </PageTitle>
              <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
                {me?.__typename === 'User' &&
                  !me.memberships.some((m) => m.account.id === accountId) &&
                  me.internalAdminRole &&
                  accountId && (
                    <SuperAdminWarningBanner
                      internalAdminRole={me.internalAdminRole}
                      accountId={accountId}
                    />
                  )}
                {shouldShowRequestedReviewBanner && (
                  <RequestedReviewerBanner
                    className="mx-8 mt-6"
                    reviewButtonDisabled={reviewButtonDisabled}
                  />
                )}
                <DocumentTitle
                  title={`${graph?.title ?? graphId}@${graphVariant}`}
                >
                  <Switch>
                    {/* If route is for the wrong type, render not found */}
                    {!isProposal && <Route component={NotFound} />}
                    <Route
                      exact={true}
                      path={proposalRouteConfig.definition}
                      render={() => (
                        <Navigate
                          replace={true}
                          to={proposalHomepageRouteConfig.locationFrom({
                            location,
                            fromRouteConfig: proposalRouteConfig,
                          })}
                        />
                      )}
                    />
                    <Route
                      path={proposalHomepageRouteConfig.definition}
                      render={() => (
                        <>
                          <DocumentTitle title="Overview" />
                          <ProposalHomepage
                            graphRef={graphRef}
                            canQueryLaunches={
                              !!permissionData.graphVariantPermissions
                                ?.canQueryLaunches
                            }
                            membershipsData={membershipsData?.service ?? null}
                          />
                        </>
                      )}
                    />
                    <Route
                      path={proposalSchemaRouteConfig.definition}
                      render={() => (
                        <>
                          <DocumentTitle title="Schema" />
                          <DocumentationPage
                            historyLength={
                              variant.activeSchemaPublish?.historyLength
                            }
                          />
                        </>
                      )}
                    />
                    <Route
                      path={proposalEditorRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ApolloGraphqlDiagnosticsProvider>
                            <ApolloGraphqlActionsProvider>
                              <DocumentTitle title="Editor" />
                              <ProposalEditor
                                graphRef={graphRef}
                                mergeBaseCompositionId={
                                  graph?.variant?.proposal
                                    ?.mergeBaseCompositionId
                                }
                                isStale={isStale}
                                setInitialLatestRevisionId={
                                  setInitialLatestRevisionId
                                }
                                showPublishRevisionToast={
                                  showPublishRevisionToast
                                }
                                subgraphsHaveChanges={subgraphsHaveChanges}
                                setSubgraphsHaveChanges={
                                  setSubgraphsHaveChanges
                                }
                              />
                            </ApolloGraphqlActionsProvider>
                          </ApolloGraphqlDiagnosticsProvider>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={proposalChangesRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <>
                            <DocumentTitle title="Changes" />
                            <ProposalChanges
                              graphRef={graphRef}
                              historyLength={
                                graph.variant?.activeSchemaPublish
                                  ?.historyLength
                              }
                              isStale={isStale}
                            />
                          </>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={proposalChecksListRouteConfig.definition}
                      exact
                      render={() => (
                        <NonPublicGraphArea>
                          <PermissionGuard
                            graphId={graph?.id}
                            graphVariant={graphVariant}
                            graphVariantPermissions="canQueryLaunches"
                            fallback={PermissionBlockCard}
                          >
                            <DocumentTitle title="Checks" />
                            <ProposalChecks graphRef={graphRef} />
                          </PermissionGuard>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={proposalCheckRouteConfig.definition}
                      component={ProposalCheckLayout}
                    />
                    <Route component={NotFound} />
                  </Switch>
                </DocumentTitle>
                <VariantLayoutModals
                  currentAccountId={currentAccountId}
                  graphRef={graphRef}
                  graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
                  graphTitle={graph?.title}
                  variantUrl={variant.url}
                />
                <LazyUrlModal name={Config.modals.renameProposalTitle}>
                  {({ isOpen, closeModal }) =>
                    variant.proposal && (
                      <RenameProposalModal
                        proposalId={variant.proposal.id}
                        currentDisplayName={variant.proposal.displayName}
                        isOpen={isOpen}
                        onClose={closeModal}
                      />
                    )
                  }
                </LazyUrlModal>
              </div>
            </div>
            {latestRevision && isProposal && (
              <LazyUrlModal name={Config.modals.addReview}>
                {({ closeModal, isOpen }) => (
                  <ProposalReviewModal
                    isOpen={isOpen}
                    onClose={closeModal}
                    revisionId={latestRevision.id}
                    currentUserReview={currentUserReview}
                    isRequestedReviewer={!!shouldShowRequestedReviewBanner}
                    userId={me?.id || ''}
                  />
                )}
              </LazyUrlModal>
            )}
          </PermissionGuard>
        </PlanProtectedArea>
      </div>
    </VariantLayoutProviders>
  );
};
